import React, { useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Alert, Box, Collapse, Stack, Typography } from '@mui/material';

import GroupedViolationAlert from './GroupedViolationAlert';
import { handleExpandRowKeyDown } from './campaignScheduleTabLayout';
import type { GroupedViolation } from './campaignScheduleViolationFormatter';

interface Props {
  title: string;
  issues: Array<GroupedViolation>;
  defaultExpanded: boolean;
  itemLabel: string;
}

// See README.md#violation-display-rules.
function sectionSeverity(issues: Array<GroupedViolation>): 'error' | 'warning' | 'info' {
  if (issues.some((issue) => issue.severity === 'blocking')) {
    return 'error';
  }
  if (issues.some((issue) => issue.severity === 'warning')) {
    return 'warning';
  }
  return 'info';
}

export default function ScheduleDoctorIssueSection({ title, issues, defaultExpanded, itemLabel }: Props) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasIssues = issues.length > 0;
  const toggle = () => setExpanded((current) => !current);

  return (
    <Box>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        role={hasIssues ? 'button' : undefined}
        spacing={2}
        sx={{ bgcolor: 'background.paper', borderRadius: 1, cursor: hasIssues ? 'pointer' : 'default', px: 1, py: 1 }}
        tabIndex={hasIssues ? 0 : undefined}
        onClick={() => hasIssues && toggle()}
        onKeyDown={hasIssues ? (event) => handleExpandRowKeyDown(event, toggle) : undefined}
      >
        <Stack alignItems="center" direction="row" spacing={1}>
          {expanded ? (
            <KeyboardArrowDownIcon color={hasIssues ? undefined : 'disabled'} fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon color={hasIssues ? undefined : 'disabled'} fontSize="small" />
          )}
          <Typography fontWeight={600} variant="body1">
            {title}
          </Typography>
        </Stack>
        <Alert severity={hasIssues ? sectionSeverity(issues) : 'success'} sx={{ py: 0 }}>
          {hasIssues ? `${issues.length} ${itemLabel}${issues.length === 1 ? '' : 's'}` : 'None detected'}
        </Alert>
      </Stack>
      {hasIssues && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1.5 }}>
            <Stack spacing={1.5}>
              {issues.map((issue) => (
                <GroupedViolationAlert key={issue.key} violation={issue} />
              ))}
            </Stack>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
